import { useEffect, useMemo, useRef, useState } from 'react'
import {
  captureBlock,
  captureCard,
  getCockpit,
  type CaptureBlockParams,
  type CockpitPayload,
  type CockpitWorkstream,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import { DURABLE_DOMAINS, todayLocalISO } from '../lib'
import { getCachedCockpit } from '../useCockpitData'

type CaptureMode = 'card' | 'block'
const BLOCK_TYPES = ['Governance', 'Operations', 'Development', 'Distribution'] as const

// iOS: after the keyboard dismisses, Safari can leave the page scrolled with
// the fixed sheet displaced — double-RAF scroll reset (guide gotcha #33).
const iosBlurReset = () => {
  requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, 0)))
}

const inputCls =
  'w-full px-3 py-2 text-base bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-foreground'
const labelCls = 'text-[11px] uppercase tracking-wider text-muted-foreground mb-1'

function WorkstreamSelect({
  workstreams,
  value,
  onChange,
  durableOnly,
}: {
  workstreams: CockpitWorkstream[]
  value: string
  onChange: (path: string) => void
  durableOnly: boolean
}) {
  const groups = useMemo(() => {
    const eligible = workstreams.filter(
      (w) => w.state !== 'archived' && (!durableOnly || DURABLE_DOMAINS.includes(w.domain))
    )
    const domains = [...new Set(eligible.map((w) => w.domain))].sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    )
    return domains.map((domain) => ({
      domain,
      // Active first, then inactive, alphabetical within each.
      items: eligible
        .filter((w) => w.domain === domain)
        .sort(
          (a, b) =>
            (a.state === 'active' ? 0 : 1) - (b.state === 'active' ? 0 : 1) ||
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        ),
    }))
  }, [workstreams, durableOnly])

  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} onBlur={iosBlurReset} className={inputCls}>
      <option value="">Select workstream…</option>
      {groups.map((g) => (
        <optgroup key={g.domain} label={g.domain}>
          {g.items.map((w) => (
            <option key={w.path} value={w.path}>
              {w.name}
              {w.state === 'inactive' ? ' (inactive)' : ''}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  )
}

/** Bottom-anchored capture sheet — Card (jot a note into a workstream) or
 * Block (full creation: folder + note + calendar event), posting straight to
 * /concierge/capture/* with no agent in the loop. */
export function CaptureSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated?: (kind: CaptureMode) => void
}) {
  const [mode, setMode] = useState<CaptureMode>('card')
  const [workstreams, setWorkstreams] = useState<CockpitWorkstream[]>(
    () => getCachedCockpit()?.workstreams ?? []
  )
  const [wsPath, setWsPath] = useState('')
  // Card fields
  const [content, setContent] = useState('')
  const [cardTitle, setCardTitle] = useState('')
  // Block fields
  const [title, setTitle] = useState('')
  const [blockType, setBlockType] = useState<(typeof BLOCK_TYPES)[number]>('Operations')
  const [date, setDate] = useState(todayLocalISO())
  const [time, setTime] = useState('')
  const [durationMin, setDurationMin] = useState('')
  const [fast, setFast] = useState(false)
  const [outputIntent, setOutputIntent] = useState('')
  // Submission state
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const wsLoaded = useRef(false)

  useEffect(() => {
    if (!open) return
    // Fresh form per open; keep mode + workstream (common to capture runs).
    setContent('')
    setCardTitle('')
    setTitle('')
    setDate(todayLocalISO())
    setTime('')
    setDurationMin('')
    setFast(false)
    setOutputIntent('')
    setError(null)
    setSuccess(null)
    // Workstream picker source: module cache, else one fetch (server-side
    // SWR cache makes this cheap on /concierge where no hook is mounted).
    const cached = getCachedCockpit()
    if (cached) {
      setWorkstreams(cached.workstreams)
    } else if (!wsLoaded.current) {
      wsLoaded.current = true
      getCockpit()
        .then((p: CockpitPayload) => setWorkstreams(p.workstreams))
        .catch(() => undefined)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const valid =
    mode === 'card'
      ? !!(wsPath && content.trim())
      : !!(wsPath && title.trim() && date && (!time || Number(durationMin) > 0))

  const handleSubmit = async () => {
    if (!valid || submitting) return
    // Disabled-while-in-flight is load-bearing: create_block has no
    // idempotency — a double-tap would mint two folders + two events.
    setSubmitting(true)
    setError(null)
    try {
      if (mode === 'card') {
        const res = await captureCard({
          workstream_path: wsPath,
          content: content.trim(),
          title: cardTitle.trim() || undefined,
        })
        setSuccess(res.filename || 'Card captured.')
      } else {
        const params: CaptureBlockParams = {
          workstream_path: wsPath,
          title: title.trim(),
          type: blockType,
          date,
          fast,
          output_intent: outputIntent.trim() || undefined,
        }
        if (time) {
          params.time = time
          params.duration_minutes = Number(durationMin)
        }
        const res = await captureBlock(params)
        setSuccess(res.folder_name || 'Block created.')
      }
      onCreated?.(mode)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="concierge-root bg-background text-foreground border border-border w-full md:w-[460px] max-w-full md:max-w-[92vw] max-h-[88dvh] overflow-y-auto p-5 pb-safe shadow-lg">
        {success ? (
          <div className="space-y-4">
            <div className="text-[15px]">Captured</div>
            <div className="text-[13px] text-muted-foreground break-all">{success}</div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSuccess(null)}
                className="text-[12px] px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground transition-colors"
              >
                Capture another
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-[12px] px-3 py-1.5 border border-foreground text-foreground transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex border border-border">
                {(['card', 'block'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={cn(
                      'px-4 py-1.5 text-[12px] uppercase tracking-wider transition-colors',
                      mode === m ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="text-muted-foreground hover:text-foreground text-[18px] leading-none px-1"
              >
                ×
              </button>
            </div>

            <div className="space-y-3">
              <label className="block">
                <div className={labelCls}>Workstream</div>
                <WorkstreamSelect
                  workstreams={workstreams}
                  value={wsPath}
                  onChange={setWsPath}
                  durableOnly={mode === 'block'}
                />
                {mode === 'block' && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    New blocks route to durable domains only.
                  </div>
                )}
              </label>

              {mode === 'card' ? (
                <>
                  <label className="block">
                    <div className={labelCls}>Note</div>
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      onBlur={iosBlurReset}
                      rows={5}
                      placeholder="Markdown — becomes a YYMMDD-HHMMSS card in the workstream"
                      className={cn(inputCls, 'resize-y min-h-[96px]')}
                    />
                  </label>
                  <label className="block">
                    <div className={labelCls}>Title (optional)</div>
                    <input
                      type="text"
                      value={cardTitle}
                      onChange={(e) => setCardTitle(e.target.value)}
                      onBlur={iosBlurReset}
                      placeholder="Defaults to the first line"
                      className={inputCls}
                    />
                  </label>
                </>
              ) : (
                <>
                  <label className="block">
                    <div className={labelCls}>Title</div>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      onBlur={iosBlurReset}
                      className={inputCls}
                    />
                  </label>
                  <div>
                    <div className={labelCls}>Type</div>
                    <div className="flex border border-border">
                      {BLOCK_TYPES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setBlockType(t)}
                          title={t}
                          className={cn(
                            'flex-1 px-1 py-1.5 text-[11px] transition-colors',
                            blockType === t
                              ? 'bg-foreground text-background'
                              : 'text-muted-foreground hover:text-foreground'
                          )}
                        >
                          {t.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <label className="block col-span-1">
                      <div className={labelCls}>Date</div>
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        onBlur={iosBlurReset}
                        className={inputCls}
                      />
                    </label>
                    <label className="block col-span-1">
                      <div className={labelCls}>Time</div>
                      <input
                        type="time"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        onBlur={iosBlurReset}
                        className={inputCls}
                      />
                    </label>
                    <label className="block col-span-1">
                      <div className={labelCls}>Minutes</div>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        value={durationMin}
                        onChange={(e) => setDurationMin(e.target.value)}
                        onBlur={iosBlurReset}
                        disabled={!time}
                        placeholder={time ? 'required' : 'all-day'}
                        className={cn(inputCls, !time && 'opacity-50')}
                      />
                    </label>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={fast}
                      onChange={(e) => setFast(e.target.checked)}
                      className="accent-foreground"
                    />
                    <span className="text-[12px] text-foreground">Fast task (⚡ on the event title)</span>
                  </label>
                  <label className="block">
                    <div className={labelCls}>Output (optional)</div>
                    <textarea
                      value={outputIntent}
                      onChange={(e) => setOutputIntent(e.target.value)}
                      onBlur={iosBlurReset}
                      rows={2}
                      placeholder="Blank = placeholder, define later"
                      className={cn(inputCls, 'resize-y')}
                    />
                  </label>
                </>
              )}

              {error && <div className="text-[12px] text-red-400 break-words">{error}</div>}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="text-[12px] px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={!valid || submitting}
                className={cn(
                  'text-[12px] px-3 py-1.5 border transition-colors',
                  valid && !submitting
                    ? 'border-foreground text-foreground hover:bg-[rgba(255,255,255,0.05)] cursor-pointer'
                    : 'border-border text-muted-foreground cursor-not-allowed opacity-60'
                )}
              >
                {submitting ? 'Creating…' : mode === 'card' ? 'Capture card' : 'Create block'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
